import * as React from "react"
import { EditorContent, useEditor } from "@tiptap/react"
import { StarterKit } from "@tiptap/starter-kit"
import { Image } from "@tiptap/extension-image"
import { TaskItem } from "@tiptap/extension-task-item"
import { TaskList } from "@tiptap/extension-task-list"
import { TextAlign } from "@tiptap/extension-text-align"
import { Typography } from "@tiptap/extension-typography"
import { Highlight } from "@tiptap/extension-highlight"
import { Subscript } from "@tiptap/extension-subscript"
import { Superscript } from "@tiptap/extension-superscript"
import { Underline } from "@tiptap/extension-underline"
import { MiniToolbar } from "./MiniToolbar"

interface RichTextEditorProps {
  value: string | object; // Accepts HTML or JSON
  onChange: (value: object) => void; // Save as JSON
  onBlur?: (e: React.FocusEvent) => void;
  placeholder?: string;
  editor?: any; // Optional external editor instance
  noteId?: string | number; // Add noteId to detect note switch
  editorReady?: (editor: any) => void; // Callback for when editor is ready
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "Start writing...",
  editor: externalEditor,
  noteId,
  editorReady,
}) => {
  const internalEditor = useEditor({
    extensions: [
      StarterKit,
      Image,
      TaskList,
      TaskItem,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Typography,
      Highlight,
      Subscript,
      Superscript,
      Underline,
    ],
    content: value,
    editorProps: {
      attributes: {
        placeholder,
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getJSON()); // Always save as JSON
    },
  });

  const editor = externalEditor || internalEditor;

  // Only update content when noteId changes (i.e., switching notes)
  const lastNoteId = React.useRef<string | number | undefined>();
  React.useEffect(() => {
    if (editor && noteId !== lastNoteId.current) {
      if (typeof value === "object" && value !== null) {
        editor.commands.setContent(value, false);
      } else if (typeof value === "string") {
        editor.commands.setContent(value, false);
      }
      lastNoteId.current = noteId;
    }
  }, [noteId, value, editor]);

  // Call editorReady when editor is available
  React.useEffect(() => {
    if (editor && editorReady) {
      editorReady(editor);
    }
  }, [editor, editorReady]);

  if (!editor) return <div>Loading editor...</div>;
  return (
    <div className="flex flex-col h-full rounded-xl border border-hintword-orange/50 bg-background/60 p-2 transition-all duration-200 shadow-sm">
      <MiniToolbar editor={editor} />
      <div className="flex-1 overflow-y-auto editor-scroll-area">
        <EditorContent editor={editor} onBlur={onBlur} />
      </div>
    </div>
  );
} 